
import { useEffect, useState } from "react";
import { api, PredefinedReport } from "@/services/api";
import { useAgentStore } from "@/store/agentStore";
import "./ReportsPanel.css";

interface ReportsPanelProps {
  companyId?: string;
  warehouseId?: string;
}

export function ReportsPanel({ companyId, warehouseId }: ReportsPanelProps) {
  const [reports, setReports] = useState<PredefinedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  const selectReport = useAgentStore((s) => s.selectReport);
  const phase = useAgentStore((s) => s.current?.phase);
  const isRunning = phase === "running";

  const effectiveWarehouseId = warehouseId ?? "0000-0000-0000";

  useEffect(() => {
    setLoading(true);
    api
      .getReports({ companyId, warehouseId: effectiveWarehouseId })
      .then(setReports)
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, [companyId, effectiveWarehouseId]);

  const handleClick = (report: PredefinedReport) => {
    if (isRunning) return;
    setActiveId(report.id);
    selectReport(report.title, report.id);
  };

  // Group by category
  const grouped = reports.reduce<Record<string, PredefinedReport[]>>((acc, r) => {
    (acc[r.category] ??= []).push(r);
    return acc;
  }, {});

  return (
    <section className="reports-panel">
      <div className="reports-panel-header">
        <div className="sidebar-label">PREDEFINED REPORTS</div>
      </div>

      {loading ? (
        <div className="reports-loading">Loading…</div>
      ) : (
        <div className="reports-scroll">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="reports-category">
              <div className="reports-category-label">{category}</div>
              {items.map((report) => (
                <button
                  key={report.id}
                  onClick={() => handleClick(report)}
                  disabled={isRunning}
                  className={`report-btn${activeId === report.id ? " report-btn--active" : ""}`}
                  title={report.description}
                >
                  <span className="report-btn-icon">▶</span>
                  <div className="report-btn-text">
                    <span className="report-btn-title">{report.title}</span>
                    <span className="report-btn-desc">{report.description}</span>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
